import { TextFieldProps as MuiTextFieldProps } from '@material-ui/core/TextField/TextField';
import { CancelOutlined as CancelIcon } from '@material-ui/icons';
import { debounce } from 'lodash';
import { ChangeEvent, FC, KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { withStyles, WithStyles } from '@core/theme/utils/with-styles';
import { TextField } from '@shared/components/text-field';
import { InputSize } from '@shared/types/common/input';
import { isKeyPress } from '@shared/utils/browser-events';

import { styles } from './SearchField.styles';

export interface SearchFieldProps
  extends WithStyles<typeof styles>,
    Omit<MuiTextFieldProps, 'classes' | 'onChange' | 'size'> {
  // The first Escape clears the search and stops there; the next one reaches what the search sits in
  clearOnEscape?: boolean;
  disabled?: boolean;
  placeholder: string;
  search: string;
  size?: InputSize;
  onChange: (search: string) => void;
}

const SearchFieldComponent: FC<SearchFieldProps> = ({
  autoFocus = false,
  classes,
  clearOnEscape = false,
  disabled = false,
  fullWidth,
  InputProps,
  inputProps,
  size = InputSize.small,
  placeholder,
  search,
  onBlur,
  onChange,
}) => {
  const [localSearch, setLocalSearch] = useState<string>(search);

  const changeLocalSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setLocalSearch(event.target.value);
    debouncedSearch(event.target.value);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (search !== localSearch) {
        setLocalSearch(search);
      }
    }, 400);

    return () => clearTimeout(timeout);
  }, [search]);

  const debouncedSearch = useCallback(
    debounce((value) => onChange(value), 350),
    [onChange]
  );

  const handleClearField = () => {
    debouncedSearch.cancel();
    setLocalSearch('');
    onChange('');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    inputProps?.onKeyDown?.(event);

    if (clearOnEscape && isKeyPress(event, 'Escape') && localSearch !== '') {
      event.preventDefault();
      event.stopPropagation();
      handleClearField();
    }
  };

  const endAdornment = useMemo(() => {
    if (localSearch) {
      return <CancelIcon onClick={handleClearField} classes={{ root: classes.clearIcon }} />;
    }
  }, [handleClearField, localSearch]);

  return (
    <TextField
      autoFocus={autoFocus}
      classes={{
        root: classes.root,
        input: classes.inputClass,
      }}
      disabled={disabled}
      fullWidth={fullWidth}
      inputProps={{ ...inputProps, onKeyDown: handleKeyDown }}
      InputProps={{
        endAdornment: endAdornment,
        ...InputProps,
      }}
      placeholder={placeholder}
      size={size}
      value={localSearch}
      withSearchIcon
      onChange={changeLocalSearch}
      onBlur={onBlur}
    />
  );
};

export const SearchField = withStyles(styles)(SearchFieldComponent);
